import React, { useState, useEffect } from 'react';
import {
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  Menu,
  X
} from 'lucide-react';

const DARK_HERO_PAGES = ['/', '/managers'];
const TRANSPARENT_NAV_PAGES = ['/', '/managers', '/residents'];

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Transparent nav on pages with hero sections
  const isTransparentPage = TRANSPARENT_NAV_PAGES.includes(location.pathname);
  const isTransparent = isTransparentPage && !scrolled && !mobileMenuOpen;
  const isDarkHero = DARK_HERO_PAGES.includes(location.pathname) && isTransparent;

  // Text color logic: dark hero = white text, light hero = dark text, scrolled = dark text
  const navTextClass = isDarkHero
    ? 'text-white/80 hover:text-white'
    : 'text-neutral-600 hover:text-primary-600';
  const activeNavTextClass = isDarkHero
    ? 'text-white'
    : 'text-primary-600';

  const handlePricingClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isTransparent
        ? 'bg-transparent'
        : 'bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer"
          >
            <img
              src={isDarkHero ? "/bilmwhite.png" : "/bilmlogo.png"}
              alt="Bilm Logo"
              width="80"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/residents"
              className={`nav-link text-sm font-medium transition-colors ${
                isActive('/residents') ? activeNavTextClass : navTextClass
              }`}
            >
              For Residents
            </Link>
            <Link
              to="/managers"
              className={`nav-link text-sm font-medium transition-colors ${
                isActive('/managers') ? activeNavTextClass : navTextClass
              }`}
            >
              For Managers
            </Link>
            <button
              onClick={handlePricingClick}
              className={`nav-link text-sm font-medium transition-colors ${navTextClass}`}
            >
              Pricing
            </button>
            <Link
              to="/support"
              className={`nav-link text-sm font-medium transition-colors ${
                isActive('/support') ? activeNavTextClass : navTextClass
              }`}
            >
              Support
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <button
              className={`hidden md:block text-sm font-medium transition-colors ${
                isDarkHero
                  ? 'text-white/80 hover:text-white'
                  : 'text-neutral-700 hover:text-primary-600'
              }`}
              onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-in'}
            >
              Log in
            </button>
            <button
              className={`inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-small transition-all ${
                isDarkHero
                  ? 'text-primary-900 bg-white hover:bg-neutral-100 shadow-lg'
                  : 'text-white bg-primary-600 hover:bg-primary-700 shadow-custom'
              }`}
              onClick={() => window.location.href = 'https://admin.bilm.app/auth/sign-up'}
            >
              Get Started
            </button>
            <button
              className={`md:hidden p-2 rounded-small ${
                isDarkHero
                  ? 'text-white hover:bg-white/10'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div className={`md:hidden border-t border-neutral-100 bg-white px-4 py-4 space-y-1 ${mobileMenuOpen ? 'block' : 'hidden'}`}>
          <Link to="/" className="block w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-small">Home</Link>
          <Link to="/residents" className="block w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-small">For Residents</Link>
          <Link to="/managers" className="block w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-small">For Managers</Link>
          <button onClick={handlePricingClick} className="block w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-small">Pricing</button>
          <Link to="/support" className="block w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-small">Support</Link>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
